import { Typography } from "@mui/material";
import { ConfirmDialog } from "Components/Shared/Dialogs/ConfirmDialog";
import { Resources, useResource } from "Translations/Resources";
import { type JSX, useCallback, useState } from "react";

type Props = {
  isDirty: boolean;
  onClose: () => void;
  confirmationMessage?: string;
};

type UseFormModalCloseReturn = {
  /** Guards any navigation away from the form — a back arrow, a step change — not only
      closing. The action runs immediately when the form is clean. */
  requestNavigation: (action: () => void) => void;
  handleCloseRequest: () => void;
  ConfirmDialogComponent: JSX.Element;
};

export const useFormModalClose = ({
  isDirty,
  onClose,
  confirmationMessage,
}: Props): UseFormModalCloseReturn => {
  // Holds the action to run on confirm, rather than a bare open flag, so one dialog can
  // guard several different exits.
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const { t } = useResource();

  const requestNavigation = useCallback(
    (action: () => void) => {
      if (isDirty) {
        // The updater form would call `action`; returning it stores it instead.
        setPendingAction(() => action);

        return;
      }

      action();
    },
    [isDirty],
  );

  const handleCloseRequest = useCallback(
    () => requestNavigation(onClose),
    [requestNavigation, onClose],
  );

  const confirmationContent = confirmationMessage || (
    <Typography>{t(Resources.Common.UnsavedChangesWarning)}</Typography>
  );

  const ConfirmDialogComponent = (
    <ConfirmDialog
      isOpen={!!pendingAction}
      modalTitle={t(Resources.Common.UnsavedChangesTitle)}
      modalContent={confirmationContent}
      handleResult={(isConfirmed) => {
        if (isConfirmed) {
          pendingAction?.();
        }

        setPendingAction(null);
      }}
      confirmButtonText={t(Resources.Common.DiscardChanges)}
    />
  );

  return { requestNavigation, handleCloseRequest, ConfirmDialogComponent };
};
